import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';

// components
import CartItemEventSession from './CartItemEventSession';
import OptionalFullProgram from './OptionalFullProgram';
import { LabelSmall } from '@styled';

// functionality
import { createCartItem } from '@general/api/cartAPI';
import { createEventLineItemPayload } from '@general/helpers/cart.helpers';
import productTypes from '@general/productTypes';
import { CartItemEventContext } from './CartItemEventProvider';

const queryParams = qs.parse(window.location.search.slice(1));

const CartItemEventSessions = ({
    onComponentLoaded = () => {},
    registeredEvents,
    setFullProgramRequired,
    setOptionalFullProgramEvents,
    setSeatsRemaining,
}) => {
    // ------------------------------
    // State
    // ------------------------------

    const { eventDataForMember, eventRegistrationData } = useContext(CartItemEventContext);
    const [optionalFullProgram, setOptionalFullProgram] = useState();
    const [sessionsLoaded, setSessionsLoaded] = useState([]);
    const [selectedConflictCodes, setSelectedConflictCodes] = useState([]);
    const [hasRegistered, setHasRegistered] = useState(false);

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        onComponentLoaded();
    }, [onComponentLoaded]);

    // Check to see if there is an optional full program
    useEffect(() => {
        if (!eventRegistrationData) {
            return;
        }

        const optionalProgram = eventRegistrationData.event.functions.find(
            session => session.code === 'FP' && session.fees.some(f => !f.auto_enroll)
        );

        const seatsRemaining = eventRegistrationData.event.functions.find(session => session.code === 'FP')
            .seats_remaining;

        if (optionalProgram) {
            setFullProgramRequired(false);
            setOptionalFullProgram(optionalProgram);
        } else {
            setFullProgramRequired(true);
        }

        setSeatsRemaining(seatsRemaining);
    }, [eventRegistrationData, optionalFullProgram, setFullProgramRequired, setSeatsRemaining]);

    useEffect(() => {
        if (!eventRegistrationData) {
            return null;
        }

        const sessionCodesInCart = eventDataForMember.sessions
            .filter(s => s.product_type === productTypes.EVENT_SESSION)
            .map(s => s.name);

        const conflictCodesInCart = eventRegistrationData.event.functions
            .filter(func => !!func.fees.find(fee => sessionCodesInCart.includes(fee.product_code)))
            .map(func => func.conflict_code);

        const registeredConflictCodes = eventRegistrationData.event.functions
            .filter(eventFunction => eventRegistrationData.memberEventSessions.includes(eventFunction.id))
            .map(eventFunction => eventFunction.conflict_code);

        const combinedConflictCodes = new Set([...conflictCodesInCart, ...registeredConflictCodes]);
        const allConflictCodes = Array.from(combinedConflictCodes);

        setSelectedConflictCodes(allConflictCodes);
    }, [eventDataForMember, eventRegistrationData]);

    // Check to see if user has attended the full program
    useEffect(() => {
        if (!eventRegistrationData) {
            return;
        }

        if (registeredEvents.length === 0) {
            setHasRegistered(false);
            return;
        }

        const registered = registeredEvents.includes(eventRegistrationData.event.code);
        setHasRegistered(registered);
    }, [eventRegistrationData, registeredEvents]);

    // Auto-add session to cart if it is in the URL query params
    useEffect(() => {
        console.log('eventRegistrationData', eventRegistrationData);

        (async () => {
            if (!eventRegistrationData || !queryParams.event || !queryParams.session) {
                return;
            }

            const sessionId = queryParams.session;

            // Return early if member is already registered for the session
            if (eventRegistrationData.memberEventSessions.includes(sessionId)) {
                console.log('Member already registered for session, not adding again');
                return;
            }

            // Return early if session is already in cart
            if (
                eventRegistrationData.cart.items.some(
                    item =>
                        item.product_type === productTypes.EVENT_SESSION &&
                        item.meta_info.event_function_id === sessionId
                )
            ) {
                console.log('Session already in cart, not adding again');
                return;
            }

            const session = eventRegistrationData.optionalSessions.find(s => s.id === sessionId);
            if (!session) {
                console.error(`Session with ID ${sessionId} not found in optional sessions`);
                return;
            }

            const cartItemData = [
                createEventLineItemPayload({
                    event: eventRegistrationData.event,
                    member: eventRegistrationData.member,
                    registrantClasses: eventRegistrationData.registrantClass,
                    session,
                }),
            ];

            await createCartItem({
                cartId: eventRegistrationData.cart.id,
                cartItemData,
                viewerId: eventRegistrationData.viewer.recordId,
            });
        })();
    }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

    // ------------------------------
    // Functions
    // ------------------------------

    const trackConflictCode = ({ conflictCode, selected }) => {
        if (selected) {
            setSelectedConflictCodes([...selectedConflictCodes, conflictCode]);
        } else {
            setSelectedConflictCodes(selectedConflictCodes.filter(c => c !== conflictCode));
        }
    };

    const trackLoadedSessions = sessionId => {
        if (sessionsLoaded.includes(sessionId)) {
            return;
        }

        const newArray = [...sessionsLoaded, sessionId];
        if (newArray.length === eventRegistrationData?.optionalSessions.length) {
            onComponentLoaded();
        }

        setSessionsLoaded(newArray);
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!eventRegistrationData || !eventRegistrationData?.optionalSessions?.length) {
        return null;
    }

    return (
        <div className="pt-1rem">
            {optionalFullProgram && (
                <div className="mb-1rem">
                    <OptionalFullProgram
                        fullProgram={optionalFullProgram}
                        onComponentLoaded={() => trackLoadedSessions(optionalFullProgram.id)}
                        registered={hasRegistered}
                        setOptionalFullProgramEvents={setOptionalFullProgramEvents}
                    />
                </div>
            )}

            <LabelSmall className="block">Optional Sessions</LabelSmall>

            {eventRegistrationData.optionalSessions.map(session => (
                <div key={session.id} className="mt-1rem">
                    <CartItemEventSession
                        alreadyRegistered={eventRegistrationData.memberEventSessions.includes(session.id)}
                        hasConflict={session.conflict_code && selectedConflictCodes.includes(session.conflict_code)}
                        session={session}
                        onComponentLoaded={() => trackLoadedSessions(session.id)}
                        onSessionToggle={selected => {
                            trackConflictCode({ conflictCode: session.conflict_code, selected });
                        }}
                    />
                </div>
            ))}
        </div>
    );
};

CartItemEventSessions.propTypes = {
    onComponentLoaded: PropTypes.func,
    registeredEvents: PropTypes.arrayOf(PropTypes.string),
    setFullProgramRequired: PropTypes.func,
    setSeatsRemaining: PropTypes.func,
    setOptionalFullProgramEvents: PropTypes.func,
};

export default CartItemEventSessions;
